import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Plus, Edit2, Trash2, ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCommunityItem = {
  title: '',
  description: '',
  image_url: '',
  date: ''
};

export const AdminCommunity = () => {
  const [communityItems, setCommunityItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyCommunityItem);
  const [saving, setSaving] = useState(false);

  const fetchCommunityItems = useCallback(async () => {
    try {
      const response = await fetch(`${API}/community`);
      const data = await response.json();
      setCommunityItems(data);
    } catch (error) {
      console.error('Failed to fetch community items:', error);
      toast.error('Failed to load community items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommunityItems();
  }, [fetchCommunityItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingItem 
        ? `${API}/admin/community/${editingItem.community_id}`
        : `${API}/admin/community`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save community item');

      toast.success(editingItem ? 'Community item updated!' : 'Community item added!');
      setIsDialogOpen(false);
      setEditingItem(null);
      setFormData(emptyCommunityItem);
      fetchCommunityItems();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save community item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (communityId) => {
    if (!confirm('Are you sure you want to delete this community item?')) return;

    try {
      const response = await fetch(`${API}/admin/community/${communityId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Community item deleted!');
      fetchCommunityItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete community item');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      image_url: item.image_url || '',
      date: item.date || ''
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData(emptyCommunityItem);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-community">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#FF00FF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#FF00FF] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-[#FF00FF]" />
            <h1 className="font-permanent-marker text-3xl text-black">Manage Community</h1>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openAddDialog}
                className="btn-graffiti-pink flex items-center gap-2"
                data-testid="add-community-btn"
              >
                <Plus size={20} /> Add Item
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-4 border-black rounded-xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bangers text-2xl">
                  {editingItem ? 'Edit Community Item' : 'Add New Community Item'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="community-form">
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="Local Food Drive Partnership"
                    data-testid="input-title"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="input-graffiti min-h-[120px]"
                    placeholder="Tell the story of this community involvement..."
                    data-testid="input-description"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Date</label>
                  <Input
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-graffiti"
                    placeholder="January 2026"
                    data-testid="input-date"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Image</label>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
                {formData.image_url && (
                  <div className="rounded-lg border-2 border-black overflow-hidden">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-32 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-4 py-2 border-4 border-black rounded-lg font-bangers text-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-graffiti-pink flex items-center justify-center gap-2"
                    data-testid="save-community-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    Save
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Community Items List */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#FF00FF] animate-spin mx-auto" />
          </div>
        ) : communityItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No community items yet.</p>
              <p className="font-space-grotesk text-gray-500 mt-2">Add your first community involvement!</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {communityItems.map((item, index) => (
              <motion.div
                key={item.community_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-graffiti flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`community-item-${index}`}
              >
                {item.image_url && (
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-black shrink-0"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-bangers text-xl text-black">{item.title}</h3>
                  <p className="font-space-grotesk text-gray-600 text-sm line-clamp-2">{item.description}</p>
                  {item.date && (
                    <span className="inline-block mt-1 text-xs text-[#FF00FF] font-space-grotesk font-semibold">
                      {item.date}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openEditDialog(item)}
                    className="p-2 bg-[#CCFF00] rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`edit-community-${index}`}
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.community_id)}
                    className="p-2 bg-red-500 text-white rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`delete-community-${index}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCommunity;
